import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMetronome } from '../contexts/MetronomeContext';
import { useLanguage } from '../contexts/LanguageContext';

const BeatIndicators = () => {
  const { 
    beatsPerMeasure, 
    accentBeats, 
    toggleAccent,
    toggleSubdivisionAccent,
    subdivisionAccents,
    currentBeat,
    currentSubdivision,
    currentRhythmType,
    isPlaying
  } = useMetronome();
  const { t } = useLanguage();
  
  // Track which group of beats is currently shown
  const [currentBeatGroup, setCurrentBeatGroup] = useState(0);
  
  // Create array of beats from 0 to beatsPerMeasure-1
  const allBeats = Array.from({ length: beatsPerMeasure }, (_, i) => i);
  
  // Get subdivision count from current rhythm type
  const subdivisionCount = currentRhythmType.subdivisions;
  
  // Mobile-optimized: Show fewer beats per group on mobile
  const getMaxBeatsPerGroup = () => {
    if (window.innerWidth < 640) return 3; // Mobile: 3 beats
    if (window.innerWidth < 768) return 4; // Tablet: 4 beats
    return 4; // Desktop: 4 beats
  };
  
  const [maxBeatsPerGroup, setMaxBeatsPerGroup] = useState(getMaxBeatsPerGroup());
  
  // Update maxBeatsPerGroup on window resize
  useEffect(() => {
    const handleResize = () => {
      setMaxBeatsPerGroup(getMaxBeatsPerGroup());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Calculate number of groups
  const totalGroups = Math.ceil(beatsPerMeasure / maxBeatsPerGroup);
  
  // Get current group of beats to display
  const getVisibleBeats = () => {
    const startBeat = currentBeatGroup * maxBeatsPerGroup;
    const endBeat = Math.min(startBeat + maxBeatsPerGroup, beatsPerMeasure);
    return allBeats.slice(startBeat, endBeat);
  };
  
  // Currently visible beats
  const visibleBeats = getVisibleBeats();

  // Function to check if a subdivision is accented
  const isSubdivisionAccented = (beatIndex, subdivisionIndex) => {
    const key = `${beatIndex}-${subdivisionIndex}`;
    return subdivisionAccents[key] === true;
  };

  // Mobile-optimized beat size
  const getBeatSize = () => {
    return 'w-16 h-16 sm:w-14 sm:h-14 md:w-16 md:h-16';
  };

  // Mobile-optimized subdivision size
  const getSubdivisionSize = () => {
    return 'w-12 h-12 sm:w-10 sm:h-10 md:w-10 md:h-10';
  };
  
  // Auto-advance to the beat group containing the current beat
  useEffect(() => {
    if (isPlaying) {
      const targetGroup = Math.floor(currentBeat / maxBeatsPerGroup);
      if (targetGroup !== currentBeatGroup) {
        setCurrentBeatGroup(targetGroup);
      }
    }
  }, [currentBeat, isPlaying, currentBeatGroup, maxBeatsPerGroup]);
  
  // Get the subdivision label according to music notation convention
  const getSubdivisionLabel = (beatIndex, subdivisionIndex) => {
    // For first subdivision (on the beat), display the beat number
    if (subdivisionIndex === 0) {
      return beatIndex + 1;
    }
    
    // Otherwise use the label from the rhythm type if available
    if (currentRhythmType.componentLabels && 
        currentRhythmType.componentLabels[subdivisionIndex]) {
      return currentRhythmType.componentLabels[subdivisionIndex];
    }
    
    // Fallback to subdivision index
    return subdivisionIndex + 1;
  };
  
  // Handle group navigation
  const goToPreviousGroup = () => {
    setCurrentBeatGroup(prev => Math.max(0, prev - 1));
  };
  
  const goToNextGroup = () => {
    setCurrentBeatGroup(prev => Math.min(totalGroups - 1, prev + 1));
  };

  return (
    <div className="mb-4 sm:mb-6">
      {/* Group navigation (only show if there are multiple groups) */}
      {totalGroups > 1 && (
        <div className="flex justify-center items-center mb-4 gap-4">
          <motion.button 
            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 font-medium"
            onClick={goToPreviousGroup}
            disabled={currentBeatGroup === 0}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ← {t('previous')}
          </motion.button>
          <span className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded">
            {currentBeatGroup + 1} / {totalGroups}
          </span>
          <motion.button 
            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 font-medium"
            onClick={goToNextGroup}
            disabled={currentBeatGroup === totalGroups - 1}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('next')} →
          </motion.button>
        </div>
      )}

      {/* Beat Color Legend - Mobile optimized */}
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg">
          <span className="w-4 h-4 rounded-full bg-orange-100 border border-orange-300 dark:bg-orange-900/30 dark:border-orange-700"></span>
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('beatLegend.accent')}</span>
        </div>
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg">
          <span className="w-4 h-4 rounded-full bg-indigo-100 border border-indigo-300 dark:bg-indigo-900/30 dark:border-indigo-700"></span>
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('beatLegend.regular')}</span>
        </div>
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg">
          <span className="w-4 h-4 rounded-full animate-pulse ring-2 ring-opacity-50 ring-indigo-300 dark:ring-indigo-700"></span>
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('beatLegend.current')}</span>
        </div>
      </div>

      {/* Click instruction - Mobile optimized */}
      <div className="text-center mb-4 text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
        📱 {t('clickToAccent')}
      </div>

      {/* Main Beat Circles - Mobile optimized spacing */}
      <div className="flex justify-center gap-4 sm:gap-3 md:gap-5 mb-6">
        {visibleBeats.map((beat) => (
          <motion.button
            key={beat}
            onClick={() => toggleAccent(beat)}
            className={`${getBeatSize()} rounded-full flex items-center justify-center text-xl sm:text-lg md:text-lg font-bold relative ${
              accentBeats.includes(beat)
                ? 'bg-orange-100 text-orange-600 border-2 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700'
                : 'bg-indigo-100 text-indigo-600 border-2 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-700'
            } ${isPlaying && currentBeat === beat ? 'animate-pulse ring-4 ring-opacity-50 ring-indigo-300 dark:ring-indigo-700' : ''}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={accentBeats.includes(beat) ? t('beatTooltip.removeAccent') : t('beatTooltip.addAccent')}
            style={{ 
              minHeight: '4rem',
              minWidth: '4rem'
            }}
          >
            {beat + 1}
          </motion.button>
        ))}
      </div>

      {/* Subdivisions - Mobile optimized layout */}
      {subdivisionCount > 1 && (
        <div className="space-y-6 w-full max-w-4xl mx-auto">
          {visibleBeats.map((beat) => (
            <div 
              key={`beat-group-${beat}`} 
              className="flex flex-col items-center bg-gray-50 dark:bg-gray-800 p-4 rounded-xl"
            >
              {/* Beat label */}
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                Beat {beat + 1}
              </div>
              
              {/* Horizontal row of subdivisions for this beat - Mobile optimized */}
              <div className="flex justify-center items-center flex-wrap gap-3 sm:gap-2 w-full">
                {Array.from({ length: subdivisionCount }, (_, subIndex) => (
                  <motion.button
                    key={`beat-${beat}-sub-${subIndex}`}
                    onClick={() => toggleSubdivisionAccent(beat, subIndex)}
                    className={`${getSubdivisionSize()} rounded-full flex items-center justify-center text-sm font-medium relative
                      ${isPlaying && currentBeat === beat && currentSubdivision === subIndex 
                        ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 scale-110' 
                        : ''
                      }
                      ${subIndex === 0 && accentBeats.includes(beat)
                        ? 'bg-orange-100 text-orange-600 border border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700'
                        : subIndex === 0
                          ? 'bg-indigo-100 text-indigo-600 border border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-700'
                          : isSubdivisionAccented(beat, subIndex)
                            ? 'bg-orange-50 text-orange-500 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800'
                            : 'bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
                      }
                    `}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title={
                      subIndex === 0 
                        ? (accentBeats.includes(beat) 
                            ? t('beatTooltip.removeAccent') 
                            : t('beatTooltip.addAccent'))
                        : (isSubdivisionAccented(beat, subIndex) 
                            ? t('beatTooltip.removeSubdivisionAccent') 
                            : t('beatTooltip.addSubdivisionAccent'))
                    }
                    style={{ 
                      minHeight: '3rem',
                      minWidth: '3rem'
                    }}
                  >
                    {getSubdivisionLabel(beat, subIndex)}
                  </motion.button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Note type display */}
      <div className="text-center mt-6 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
        🎵 {t(`noteTypes.${currentRhythmType.id}`)}
      </div>
    </div>
  );
};

export default BeatIndicators;